/**
 * Durvient PDP.
 * Page-specific behavior only:
 * - countdown / viewing-now
 * - pincode validation
 * - sale badge sync
 * - desktop cursor-follow zoom
 * - first-interaction mobile lightbox loader
 * - reference-style sticky buy bar
 */
(() => {
  'use strict';

  if (!window.RD || window.RD.__pdpRegistered) return;
  window.RD.__pdpRegistered = true;

  const pad = (n) => String(Math.max(0, n)).padStart(2, '0');

  window.RD.register('pdp', (root) => {
    const cleanup = [];

    /* Countdown */
    const countdown = root.querySelector('[data-rd-countdown]');
    if (countdown) {
      let left = Math.max(0, Number(countdown.dataset.duration || 0));
      const h = countdown.querySelector('[data-rd-hours]');
      const m = countdown.querySelector('[data-rd-minutes]');
      const s = countdown.querySelector('[data-rd-seconds]');
      const render = () => {
        if (h) h.textContent = pad(Math.floor(left / 3600));
        if (m) m.textContent = pad(Math.floor((left % 3600) / 60));
        if (s) s.textContent = pad(left % 60);
      };
      render();
      const id = setInterval(() => {
        if (document.hidden || left <= 0) return;
        left -= 1;
        render();
      }, 1000);
      cleanup.push(() => clearInterval(id));
    }

    /* Viewing now */
    const viewers = root.querySelector('[data-rd-live-viewing]');
    const viewerCount = viewers?.querySelector('[data-rd-viewer-count]');
    if (viewers && viewerCount) {
      const min = Math.max(1, Number(viewers.dataset.viewerMin || 1));
      const max = Math.max(min, Number(viewers.dataset.viewerMax || min));
      const id = setInterval(() => {
        if (document.hidden) return;
        const current = Number(viewerCount.textContent || min);
        viewerCount.textContent = String(
          Math.min(max, Math.max(min, current + (Math.random() > .5 ? 1 : -1)))
        );
      }, 3200);
      cleanup.push(() => clearInterval(id));
    }

    /* ======================================================
       PINCODE / SHIPROCKET EDD — MANUAL PIN ONLY
       ====================================================== */

    const pincode = root.querySelector('[data-rd-pincode]');

    if (pincode) {
      const form = pincode.querySelector('[data-rd-pincode-form]');
      const input = pincode.querySelector('[data-rd-pincode-input]');
      const button = pincode.querySelector('[data-rd-pincode-submit]');
      const message = pincode.querySelector('[data-rd-pincode-message]');
      const card = pincode.querySelector('[data-rd-location-card]');
      const resultTitle = pincode.querySelector('[data-rd-result-title]');
      const resultSubtitle = pincode.querySelector('[data-rd-result-subtitle]');
      const tooltip = pincode.querySelector('.rd-pdp-location-block__tooltip');
      const tooltipSummary = tooltip?.querySelector(':scope > summary');
      const variantMapNode = pincode.querySelector('[data-rd-pincode-variants]');

      const endpoint = String(
        pincode.dataset.apiEndpoint || ''
      ).trim();

      const estimatePrefix = String(
        pincode.dataset.estimatePrefix || 'Estimated delivery'
      ).trim();

      const locationPrefix = String(
        pincode.dataset.locationPrefix || 'Delivering to'
      ).trim();

      const loadingMessage = String(
        pincode.dataset.loadingMessage ||
        'Checking earliest delivery estimate…'
      ).trim();

      const invalidMessage = String(
        pincode.dataset.invalidMessage ||
        'Enter a valid 6-digit Indian PIN code.'
      ).trim();

      const unavailableMessage = String(
        pincode.dataset.unavailableMessage ||
        'Delivery estimate is unavailable for this PIN code.'
      ).trim();

      const errorMessage = String(
        pincode.dataset.errorMessage ||
        'Delivery estimate could not be checked right now. Please try again.'
      ).trim();

      const fallbackWeight = Math.max(
        .05,
        Number(pincode.dataset.fallbackWeightKg || .5) || .5
      );

      let variantMap = new Map();
      let activeEstimateController = null;

      if (variantMapNode) {
        try {
          const variants = JSON.parse(
            variantMapNode.textContent || '[]'
          );

          variantMap = new Map(
            variants
              .filter((item) => item && item.id)
              .map((item) => [
                String(item.id),
                item
              ])
          );
        } catch (_) {}
      }

      const normalizePin = (value = '') => {
        return String(value || '')
          .replace(/\D/g, '')
          .slice(0, 6);
      };

      const validPin = (value = '') => {
        const clean = normalizePin(value);
        return /^[1-9]\d{5}$/.test(clean)
          ? clean
          : '';
      };

      const cleanPin = (value = input?.value || '') => {
        const clean = normalizePin(value);

        if (
          input &&
          value === input.value
        ) {
          input.value = clean;
        }

        return validPin(clean);
      };

      const setStatus = (
        text = '',
        isError = false
      ) => {
        if (!message) return;

        message.textContent = text;
        message.classList.toggle(
          'is-error',
          Boolean(isError)
        );
      };

      /*
        Preserve the location button icon/markup.

        The previous implementation saved only textContent and later
        replaced the complete button text, permanently removing its SVG.
      */
      const setBusy = (
        control,
        busy,
        busyLabel = ''
      ) => {
        if (!control) return;

        if (!control.dataset.rdDefaultHtml) {
          control.dataset.rdDefaultHtml =
            control.innerHTML;
        }

        control.disabled = Boolean(busy);

        control.setAttribute(
          'aria-busy',
          busy ? 'true' : 'false'
        );

        if (
          busy &&
          busyLabel
        ) {
          control.textContent = busyLabel;
        } else {
          control.innerHTML =
            control.dataset.rdDefaultHtml;
        }
      };

      const clearResult = () => {
        if (card) {
          card.hidden = true;
        }

        setStatus('');
      };

      const getPurchaseData = () => {
        const variantInput =
          root.querySelector(
            'form[data-type="add-to-cart-form"] input[name="id"]'
          ) ||
          root.querySelector(
            'input.product-variant-id[name="id"]'
          ) ||
          root.querySelector(
            'input[name="id"]'
          );

        const variantId = String(
          variantInput?.value || ''
        ).trim();

        const variant =
          variantMap.get(variantId) ||
          null;

        const quantityInput =
          root.querySelector(
            'form[data-type="add-to-cart-form"] input[name="quantity"]'
          ) ||
          root.querySelector(
            'input[name="quantity"]'
          );

        const quantity = Math.max(
          1,
          Math.floor(
            Number(
              quantityInput?.value || 1
            ) || 1
          )
        );

        const unitWeight = Math.max(
          .05,
          Number(variant?.weight_kg || 0) > 0
            ? Number(variant.weight_kg)
            : fallbackWeight
        );

        const priceCents = Math.max(
          0,
          Number(
            variant?.price_cents || 0
          ) || 0
        );

        return {
          weight: Math.min(
            100,
            unitWeight * quantity
          ),

          declaredValue:
            priceCents > 0
              ? (
                  priceCents *
                  quantity
                ) / 100
              : 0
        };
      };

      const buildRequestUrl = (
        params = {}
      ) => {
        if (!endpoint) return '';

        let url;

        try {
          url = new URL(
            endpoint,
            window.location.origin
          );
        } catch (_) {
          return '';
        }

        const purchase =
          getPurchaseData();

        url.searchParams.set(
          'weight',
          String(
            Number(
              purchase.weight.toFixed(3)
            )
          )
        );

        if (
          purchase.declaredValue > 0
        ) {
          url.searchParams.set(
            'declared_value',
            String(
              Number(
                purchase.declaredValue.toFixed(2)
              )
            )
          );
        }

        Object.entries(
          params
        ).forEach(
          ([key, value]) => {
            if (
              value !== '' &&
              value !== null &&
              value !== undefined
            ) {
              url.searchParams.set(
                key,
                String(value)
              );
            }
          }
        );

        return url.toString();
      };

      const formatLocation = (
        location = {},
        pin = ''
      ) => {
        const parts = [];

        /*
          Customer-facing output excludes the local post-office / area name.
          Example: Ashtapur, Pune, Maharashtra -> Pune, Maharashtra.
        */
        [
          location.district,
          location.state
        ]
          .map(
            (value) =>
              String(
                value || ''
              ).trim()
          )
          .filter(Boolean)
          .forEach(
            (value) => {
              if (
                !parts.some(
                  (item) =>
                    item.toLowerCase() ===
                    value.toLowerCase()
                )
              ) {
                parts.push(value);
              }
            }
          );

        const base =
          parts.join(', ');

        if (
          base &&
          pin
        ) {
          return `${base} · ${pin}`;
        }

        return base || pin;
      };

      const renderResult = (
        payload
      ) => {
        const pin = String(
          payload?.pincode || ''
        ).trim();

        const location =
          payload?.location || {};

        const locationText =
          formatLocation(
            location,
            pin
          );

        if (!payload?.serviceable) {
          if (card) {
            card.hidden = false;
          }

          if (resultTitle) {
            resultTitle.textContent =
              'Delivery estimate unavailable';
          }

          if (resultSubtitle) {
            resultSubtitle.textContent =
              locationText
                ? `${locationPrefix} ${locationText}`
                : unavailableMessage;
          }

          setStatus('');
          return;
        }

        const estimatedRange =
          String(
            payload?.estimated_range_display ||
            payload?.estimated_display ||
            payload?.estimated_date ||
            ''
          ).trim();

        if (resultTitle) {
          resultTitle.textContent =
            estimatedRange
              ? `${estimatePrefix} — ${estimatedRange}`
              : estimatePrefix;
        }

        if (resultSubtitle) {
          resultSubtitle.textContent =
            locationText
              ? `${locationPrefix} ${locationText}`
              : '';
        }

        if (card) {
          card.hidden = false;
        }

        setStatus('');
      };

      /*
        Restondev request.

        Returns payload on success, null on a handled error.
      */
      const requestEstimate = async (
        params
      ) => {

        const requestUrl =
          buildRequestUrl(params);

        if (!requestUrl) {
          clearResult();

          setStatus(
            'Delivery API endpoint is not configured.',
            true
          );

          return null;
        }

        activeEstimateController?.abort();

        const controller =
          new AbortController();

        activeEstimateController =
          controller;

        const timeoutId =
          window.setTimeout(
            () => controller.abort(),
            22000
          );
        setBusy(button, true, 'CHECKING…');

        setStatus(
          loadingMessage
        );

        if (card) {
          card.hidden = true;
        }

        try {
          const response =
            await fetch(
              requestUrl,
              {
                method: 'GET',
                mode: 'cors',
                credentials: 'omit',
                cache: 'no-store',

                headers: {
                  Accept:
                    'application/json'
                },

                referrerPolicy:
                  'strict-origin-when-cross-origin',

                signal:
                  controller.signal
              }
            );

          const responseText =
            await response.text();

          let payload = null;

          try {
            payload =
              responseText
                ? JSON.parse(
                    responseText
                  )
                : null;
          } catch (_) {
            payload = null;
          }

          if (
            !response.ok ||
            !payload?.ok
          ) {
            const serverMessage =
              payload?.message ||
              payload?.error ||
              `Delivery API HTTP ${response.status}`;

            const apiError =
              new Error(
                serverMessage
              );

            apiError.code =
              payload?.error || '';

            apiError.status =
              response.status;

            throw apiError;
          }

          if (
            payload?.pincode &&
            input
          ) {
            input.value =
              normalizePin(
                payload.pincode
              );
          }

          renderResult(
            payload
          );

          return payload;
        } catch (error) {
          if (
            error?.name ===
            'AbortError'
          ) {
            console.error(
              '[Durvient EDD] timeout',
              {
                endpoint,
                params
              }
            );

            if (card) {
              card.hidden = true;
            }

            setStatus(
              'Delivery check took too long. Please try again.',
              true
            );

            return null;
          }

          console.error(
            '[Durvient EDD]',
            {
              error,
              endpoint,
              params
            }
          );

          if (card) {
            card.hidden = true;
          }

          const mappedMessages = {
            origin_not_allowed:
              'Delivery lookup is not enabled for this storefront domain.',

            server_not_configured:
              'Delivery service is temporarily unavailable.',

            invalid_pincode:
              invalidMessage,

            rate_limited:
              'Too many delivery checks. Please wait a moment and try again.'
          };

          setStatus(
            mappedMessages[
              error?.code
            ] ||
            errorMessage,
            true
          );

          return null;
        } finally {
          window.clearTimeout(
            timeoutId
          );

          if (
            activeEstimateController ===
            controller
          ) {
            activeEstimateController =
              null;
          }
          setBusy(button, false);
        }
      };

      const submitPin = () => {
        const pin =
          cleanPin();

        if (!pin) {
          clearResult();

          setStatus(
            invalidMessage,
            true
          );

          return;
        }

        requestEstimate(
          {
            pincode: pin
          },
          button
        );
      };

      const onSubmit = (
        event
      ) => {
        event.preventDefault();
        submitPin();
      };

      const onInput = () => {
        if (!input) return;

        input.value =
          normalizePin(
            input.value
          );

        clearResult();
      };

      /*
       * Keep the PIN field button-driven.
       * Pressing Enter/Return must not submit the checker form.
       */
      const onInputKeydown = (event) => {
        if (event.key !== 'Enter') return;

        event.preventDefault();
        event.stopPropagation();
      };

      const positionMobileTooltip = () => {
        if (!tooltip?.open || !tooltipSummary) return;
        if (!window.matchMedia('(max-width: 749px)').matches) {
          tooltip.style.removeProperty('--rd-tooltip-mobile-left');
          tooltip.style.removeProperty('--rd-tooltip-mobile-top');
          return;
        }

        const popover = tooltip.querySelector(
          '.rd-pdp-location-block__tooltip-popover'
        );

        if (!popover) return;

        const viewportWidth = Math.max(
          document.documentElement.clientWidth || 0,
          window.innerWidth || 0
        );
        const viewportHeight = Math.max(
          document.documentElement.clientHeight || 0,
          window.innerHeight || 0
        );
        const edge = 12;
        const gap = 8;
        const width = Math.min(
          320,
          Math.max(240, viewportWidth - (edge * 2))
        );
        const summaryRect = tooltipSummary.getBoundingClientRect();
        const measuredHeight = Math.min(
          popover.scrollHeight || 220,
          Math.max(120, viewportHeight - (edge * 2))
        );

        let left = summaryRect.right - width;
        left = Math.max(
          edge,
          Math.min(left, viewportWidth - width - edge)
        );

        let top = summaryRect.bottom + gap;
        if (top + measuredHeight > viewportHeight - edge) {
          top = summaryRect.top - measuredHeight - gap;
        }
        top = Math.max(edge, top);

        tooltip.style.setProperty(
          '--rd-tooltip-mobile-left',
          `${Math.round(left)}px`
        );
        tooltip.style.setProperty(
          '--rd-tooltip-mobile-top',
          `${Math.round(top)}px`
        );
      };

      const onTooltipToggle = () => {
        if (!tooltip?.open) return;
        requestAnimationFrame(positionMobileTooltip);
      };

      const onViewportChange = () => {
        positionMobileTooltip();
      };

      const onDocumentClick = (event) => {
        if (!tooltip?.open) return;

        /* Keep the summary as the native toggle; any other click closes it. */
        if (
          tooltipSummary &&
          tooltipSummary.contains(event.target)
        ) {
          return;
        }

        tooltip.removeAttribute('open');
      };

      form?.addEventListener(
        'submit',
        onSubmit
      );

      input?.addEventListener(
        'input',
        onInput
      );

      input?.addEventListener(
        'keydown',
        onInputKeydown
      );

      tooltip?.addEventListener(
        'toggle',
        onTooltipToggle
      );

      window.addEventListener(
        'resize',
        onViewportChange,
        { passive: true }
      );

      window.addEventListener(
        'scroll',
        onViewportChange,
        { passive: true }
      );

      document.addEventListener(
        'click',
        onDocumentClick
      );

      cleanup.push(
        () =>
          form?.removeEventListener(
            'submit',
            onSubmit
          )
      );

      cleanup.push(
        () =>
          input?.removeEventListener(
            'input',
            onInput
          )
      );

      cleanup.push(
        () =>
          input?.removeEventListener(
            'keydown',
            onInputKeydown
          )
      );

      cleanup.push(
        () =>
          tooltip?.removeEventListener(
            'toggle',
            onTooltipToggle
          )
      );

      cleanup.push(
        () =>
          window.removeEventListener(
            'resize',
            onViewportChange
          )
      );

      cleanup.push(
        () =>
          window.removeEventListener(
            'scroll',
            onViewportChange
          )
      );

      cleanup.push(
        () =>
          document.removeEventListener(
            'click',
            onDocumentClick
          )
      );

      cleanup.push(() => activeEstimateController?.abort());
    }

    /* ======================================================
       SINGLE PRODUCT GALLERY INVARIANT

       The screenshot showed a complete second gallery inside
       the right ProductInfo column (the duplicate sale / Best
       Seller badges confirm this is a gallery duplicate, not
       the sticky ATC thumbnail).

       Keep exactly one gallery: the instance inside the
       explicit media-column host.
       ====================================================== */

    const primaryGalleryHost =
      root.querySelector('[data-rd-primary-gallery-host]');

    const getPrimaryGalleryShell = () =>
      primaryGalleryHost?.querySelector(
        '[data-rd-pdp-gallery]'
      ) || null;

    const purgeDuplicateProductGalleries = () => {
      const primaryGalleryShell =
        getPrimaryGalleryShell();

      root
        .querySelectorAll(
          '[data-rd-pdp-gallery]'
        )
        .forEach((shell) => {
          if (
            primaryGalleryShell &&
            shell !== primaryGalleryShell
          ) {
            shell.remove();
          }
        });

      /*
        Defensive cleanup for malformed/stale markup where a
        media-gallery was injected without the RD wrapper.
      */

      root
        .querySelectorAll(
          '.rd-pdp__info media-gallery'
        )
        .forEach((mediaGallery) => {
          const shell =
            mediaGallery.closest(
              '.rd-pdp__gallery-shell'
            );

          (shell || mediaGallery).remove();
        });

      root
        .querySelectorAll(
          '.rd-pdp__info .rd-pdp__gallery-badges'
        )
        .forEach((badges) => {
          badges.remove();
        });
    };


    purgeDuplicateProductGalleries();

    window.requestAnimationFrame(
      purgeDuplicateProductGalleries
    );


    /* Gallery zoom.
       - Desktop/fine pointer: cursor-follow hover magnification.
       - Existing lazy click/tap lightbox behavior remains unchanged. */
    const galleryShell = root.querySelector('[data-rd-pdp-gallery]');
    const gallery = galleryShell?.querySelector('media-gallery') || root.querySelector('media-gallery');

    /* Desktop cursor-follow hover zoom.
       This only runs when the section is configured for hover zoom and the
       device actually supports a fine hover pointer. Mobile/touch behavior is
       therefore left completely unchanged. */
    const hoverZoomMedia = window.matchMedia(
      '(min-width: 990px) and (hover: hover) and (pointer: fine)'
    );
    let hoverZoomItem = null;

    const clearHoverZoom = () => {
      if (!hoverZoomItem) return;
      hoverZoomItem.classList.remove('is-rd-hover-zoomed');
      hoverZoomItem.style.removeProperty('--rd-pdp-zoom-x');
      hoverZoomItem.style.removeProperty('--rd-pdp-zoom-y');
      hoverZoomItem = null;
    };

    const onHoverZoomMove = (event) => {
      if (!root.hasAttribute('data-rd-hover-zoom') || !hoverZoomMedia.matches) {
        clearHoverZoom();
        return;
      }

      const item = event.target.closest?.('.product__media-item.is-active');
      if (!item || !gallery?.contains(item)) {
        clearHoverZoom();
        return;
      }

      const media = item.querySelector(
        '.product__modal-opener--image .product__media'
      );
      const image = media?.querySelector('img');
      if (!media || !image) {
        clearHoverZoom();
        return;
      }

      const rect = media.getBoundingClientRect();
      if (!rect.width || !rect.height) return;

      const x = Math.max(0, Math.min(100, ((event.clientX - rect.left) / rect.width) * 100));
      const y = Math.max(0, Math.min(100, ((event.clientY - rect.top) / rect.height) * 100));

      if (hoverZoomItem && hoverZoomItem !== item) clearHoverZoom();
      hoverZoomItem = item;
      item.style.setProperty('--rd-pdp-zoom-x', `${x.toFixed(2)}%`);
      item.style.setProperty('--rd-pdp-zoom-y', `${y.toFixed(2)}%`);
      item.classList.add('is-rd-hover-zoomed');
    };

    const onHoverZoomModeChange = () => {
      if (!hoverZoomMedia.matches) clearHoverZoom();
    };

    if (gallery && root.hasAttribute('data-rd-hover-zoom')) {
      gallery.addEventListener('pointermove', onHoverZoomMove, { passive: true });
      gallery.addEventListener('pointerleave', clearHoverZoom);

      if (hoverZoomMedia.addEventListener) {
        hoverZoomMedia.addEventListener('change', onHoverZoomModeChange);
      } else if (hoverZoomMedia.addListener) {
        hoverZoomMedia.addListener(onHoverZoomModeChange);
      }

      cleanup.push(() => {
        gallery.removeEventListener('pointermove', onHoverZoomMove);
        gallery.removeEventListener('pointerleave', clearHoverZoom);
        clearHoverZoom();

        if (hoverZoomMedia.removeEventListener) {
          hoverZoomMedia.removeEventListener('change', onHoverZoomModeChange);
        } else if (hoverZoomMedia.removeListener) {
          hoverZoomMedia.removeListener(onHoverZoomModeChange);
        }
      });
    }

    /* Lazy lightbox: source JSON is parsed only after the first image click/tap. */
    let lightboxPromise = null;
    let lightboxMap = null;

    const getLightboxMap = () => {
      if (lightboxMap) return lightboxMap;
      lightboxMap = new Map();
      const node = galleryShell?.querySelector('[data-rd-lightbox-sources]');
      if (!node) return lightboxMap;
      try {
        JSON.parse(node.textContent || '[]').forEach((item) => {
          if (item?.type === 'image' && item?.id && item?.src) {
            lightboxMap.set(String(item.id), item.src);
          }
        });
      } catch (_) {}
      return lightboxMap;
    };

    const loadLightbox = () => {
      if (window.RDPDPLightbox) return Promise.resolve(window.RDPDPLightbox);
      if (lightboxPromise) return lightboxPromise;

      const jsUrl = galleryShell?.dataset.rdLightboxScript;
      const cssUrl = galleryShell?.dataset.rdLightboxStyle;

      lightboxPromise = new Promise((resolve, reject) => {
        if (cssUrl && !document.querySelector('[data-rd-pdp-lightbox-css]')) {
          const link = document.createElement('link');
          link.rel = 'stylesheet';
          link.href = cssUrl;
          link.dataset.rdPdpLightboxCss = '';
          document.head.appendChild(link);
        }

        if (!jsUrl) return reject(new Error('Missing lightbox asset.'));

        if (window.RDPDPLightbox) return resolve(window.RDPDPLightbox);

        const script = document.createElement('script');
        script.src = jsUrl;
        script.async = true;
        script.dataset.rdPdpLightboxJs = '';
        script.onload = () => resolve(window.RDPDPLightbox);
        script.onerror = reject;
        document.head.appendChild(script);
      });

      return lightboxPromise;
    };

    const onGalleryClick = (event) => {
      if (!galleryShell?.hasAttribute('data-rd-mobile-lightbox')) return;

      const item = event.target.closest?.('.product__media-item');
      const opener = event.target.closest?.('.product__modal-opener--image')
        || item?.querySelector('.product__modal-opener--image');
      const image = opener?.querySelector('.product__media img');
      if (!item || !image || !gallery?.contains(item)) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      const mediaId = (item.dataset.mediaId || '').split('-').pop() || '';
      const fallback = image.currentSrc || image.src || '';
      const src = getLightboxMap().get(mediaId) || fallback;

      loadLightbox()
        .then((api) => api?.open?.({
          src,
          fallback,
          alt: image.alt || '',
          sourceElement: opener
        }))
        .catch(() => {});
    };

    if (gallery && galleryShell?.hasAttribute('data-rd-mobile-lightbox')) {
      gallery.addEventListener('click', onGalleryClick, true);
      cleanup.push(() => gallery.removeEventListener('click', onGalleryClick, true));
    }

    /* Reference-style sticky buy bar. */
    const sticky = root.querySelector('[data-rd-sticky-atc]');
    const purchase = root.querySelector('.rd-pdp__purchase');
    const stickyAtc = sticky?.querySelector('[data-rd-sticky-atc-button]');
    const stickyBuy = sticky?.querySelector('[data-rd-sticky-buy-now]');
    const stickyLabel = sticky?.querySelector('[data-rd-sticky-atc-label]');
    const stickyNow = sticky?.querySelector('[data-rd-sticky-atc-price]');
    const stickyWas = sticky?.querySelector('[data-rd-sticky-atc-compare]');

    const mainAtc = () => root.querySelector('.rd-pdp__add-button');
    const mainBuy = () => root.querySelector('[data-rd-buy-now]');

    const syncBuyNowState = () => {
      const buyNow = mainBuy();
      if (!buyNow) return;

      const atc = mainAtc();
      const disabled = !atc
        || atc.disabled
        || atc.getAttribute('aria-disabled') === 'true';

      buyNow.disabled = disabled;
    };

    const showPurchaseError = (message = '') => {
      const wrapper = root.querySelector('.product-form__error-message-wrapper');
      const target = wrapper?.querySelector('.product-form__error-message');
      if (!wrapper || !target) return;

      target.textContent = message;
      wrapper.hidden = !message;
    };

    const onBuyNowClick = async (event) => {
      const buyNow = event.target.closest?.('[data-rd-buy-now]');
      if (!buyNow || !root.contains(buyNow)) return;

      event.preventDefault();

      if (buyNow.disabled || buyNow.classList.contains('is-loading')) return;

      const form = buyNow.closest('form');
      const variantInput = form?.querySelector('input[name="id"]');

      if (!form || !variantInput || variantInput.disabled || !variantInput.value) {
        syncBuyNowState();
        return;
      }

      const rootPath = window.Shopify?.routes?.root || '/';
      const formData = new FormData(form);

      buyNow.classList.add('is-loading');
      buyNow.setAttribute('aria-busy', 'true');
      showPurchaseError('');

      try {
        const response = await fetch(`${rootPath}cart/add.js`, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: formData
        });

        const payload = await response.json().catch(() => ({}));

        if (!response.ok) {
          throw new Error(
            payload?.description
            || payload?.message
            || 'This item could not be purchased right now.'
          );
        }

        window.location.assign(`${rootPath}checkout`);
      } catch (error) {
        showPurchaseError(
          error?.message || 'This item could not be purchased right now.'
        );
        buyNow.classList.remove('is-loading');
        buyNow.removeAttribute('aria-busy');
        syncBuyNowState();
      }
    };

    root.addEventListener('click', onBuyNowClick);
    cleanup.push(() => root.removeEventListener('click', onBuyNowClick));
    syncBuyNowState();

    const syncSticky = () => {
      if (!sticky) return;

      const atc = mainAtc();
      const disabled = !atc || atc.disabled || atc.getAttribute('aria-disabled') === 'true';

      if (stickyAtc) stickyAtc.disabled = disabled;
      if (stickyBuy) stickyBuy.disabled = disabled;

      if (stickyLabel) {
        stickyLabel.textContent =
          atc?.querySelector('span')?.textContent?.trim() || 'Add to Cart';
      }

      const now = root.querySelector('.rd-pdp__price-now');
      const was = root.querySelector('.rd-pdp__price-was');

      if (stickyNow && now) stickyNow.textContent = now.textContent.trim();

      if (stickyWas) {
        const compare = was?.textContent?.trim() || '';
        stickyWas.textContent = compare;
        stickyWas.classList.toggle('is-hidden', !compare);
      }
    };

    const setSticky = (show) => {
      if (!sticky) return;
      sticky.classList.toggle('is-visible', Boolean(show));
      sticky.setAttribute('aria-hidden', String(!show));
    };

    let stickyObserver = null;

    if (sticky && purchase) {
      syncSticky();

      stickyObserver = new IntersectionObserver(([entry]) => {
        if (!entry) return;
        setSticky(!entry.isIntersecting && entry.boundingClientRect.bottom <= 0);
      }, { threshold: 0 });

      stickyObserver.observe(purchase);

      const onStickyAtc = () => {
        const button = mainAtc();
        if (!button || button.disabled || button.getAttribute('aria-disabled') === 'true') {
          syncSticky();
          return;
        }
        button.click();
      };

      const onStickyBuy = () => {
        const button = mainBuy();
        if (button && !button.disabled) button.click();
      };

      stickyAtc?.addEventListener('click', onStickyAtc);
      stickyBuy?.addEventListener('click', onStickyBuy);

      cleanup.push(() => {
        stickyObserver?.disconnect();
        stickyAtc?.removeEventListener('click', onStickyAtc);
        stickyBuy?.removeEventListener('click', onStickyBuy);
        setSticky(false);
      });
    }

    /* Variant changes fire after Dawn updates price + main submit state. */
    let unsubscribe = null;
    if (
      typeof subscribe === 'function'
      && typeof PUB_SUB_EVENTS !== 'undefined'
      && PUB_SUB_EVENTS.variantChange
    ) {
      unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
        if (!data || data.sectionId !== root.dataset.section) return;

        const hasValidVariant = Boolean(
          data.variant && data.variant.available !== false
        );

        const buyNow = mainBuy();
        if (buyNow) {
          buyNow.disabled = !hasValidVariant;
        }

        /*
          Dawn can replace/update variant-related markup.
          Reassert the one-gallery invariant after that update.
        */
        purgeDuplicateProductGalleries();

        window.requestAnimationFrame(() => {
          purgeDuplicateProductGalleries();
          syncBuyNowState();
          syncSticky();
        });

        syncSticky();

        const badge = root.querySelector('[data-rd-sale-badge]');
        if (!badge) return;

        if (!data.variant) {
          badge.hidden = true;
          return;
        }

        const price = Number(data.variant.price || 0);
        const compare = Number(data.variant.compare_at_price || 0);

        if (compare > price && compare > 0) {
          badge.textContent = `-${Math.round(((compare - price) / compare) * 100)}%`;
          badge.hidden = false;
        } else {
          badge.hidden = true;
        }
      });
    }

    if (typeof unsubscribe === 'function') cleanup.push(unsubscribe);

    return () => cleanup.forEach((fn) => fn());
  });
})();